import { createContext, PropsWithChildren, useContext, useState } from 'react';
import { TableState } from 'react-table';
import { PERMISSIONS } from 'shared/constants';
import { useToggle } from 'shared/hooks';
import { useUserPermissions } from 'shared/hooks/use-user-permissions';
import { useSessionStorage } from 'shared/hooks/use-session-storage';
import { useQueryQuotes } from 'shared/mutations/quotes';
import { GetQuotesResponse } from 'shared/services';
import { useAuthState } from 'shared/stores';
import { QuotesSummaryItem } from 'shared/types';
import { SearchFilters, SortableFields } from 'shared/types/quotes';

const defaultState: TableState<QuotesSummaryItem> = {
  selectedRowIds: {},
  pageIndex: 0,
  pageSize: 100,
  globalFilter: '',
  filters: [],
  sortBy: [
    {
      id: 'dateSubmitted',
      desc: true,
    },
  ],
  hiddenColumns: [],
};

export const defaultSearchFilters: SearchFilters = {
  quoteNumber: '',
  wholesalerCustomerNumber: '',
  jobInfoProjectName: '',
  plumberProjectType: '',
  wholesaler: '',
  plumberContractor: '',
  brand: '',
  repAgency: '',
  requestedBy: '',
  status: '',
  approvedBy: '',
  revisionApprovedBy: '',
  dateSubmitted: ['', ''],
  dateApproved: ['', ''],
  dateRevised: ['', ''],
  lastModifiedBy: '',
  jobInfoStateName: '',
};

export interface QuoteTableState {
  tableState: TableState<QuotesSummaryItem>;
  searchFilters: SearchFilters;
}

interface QuoteListContext {
  quoteTableState: QuoteTableState;
  setQuoteTableState: React.Dispatch<React.SetStateAction<QuoteTableState>>;
  formSearchFilters: SearchFilters;
  setFormSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  quoteIdToDelete: string;
  setQuoteIdToDelete: React.Dispatch<React.SetStateAction<string>>;
  quotes: GetQuotesResponse | undefined;
  isFetching: boolean;
  isLoading: boolean;
  dialogOpen: boolean;
  deleteDialogOpen: boolean;
  toggleDialog: () => void;
  toggleDeleteDialog: () => void;
}
const quoteListContext = createContext({} as QuoteListContext);

export function ProvideQuoteListState({ children }: PropsWithChildren<{}>) {
  const userPermissions = useUserPermissions();
  const userIsAdmin = userPermissions.includes(PERMISSIONS['quoting.admin']);
  const authState = useAuthState();
  const userEmail = authState.user.value?.email ?? '';
  const userId = authState.user.value?.id ?? '';
  const [searchFilters] = useSessionStorage('quoteSummaryFilters', defaultSearchFilters);
  const [quoteTableState, setQuoteTableState] = useState<QuoteTableState>({
    tableState: defaultState,
    searchFilters,
  });
  const [formSearchFilters, setFormSearchFilters] = useState<SearchFilters>(searchFilters);
  const [quoteIdToDelete, setQuoteIdToDelete] = useState<string>('');
  const [dialogOpen, toggleDialog] = useToggle();
  const [deleteDialogOpen, toggleDeleteDialog] = useToggle();

  const { quotes, isFetching, isLoading } = useQueryQuotes({
    userIsAdmin,
    page: quoteTableState.tableState.pageIndex,
    pageSize: quoteTableState.tableState.pageSize,
    sortOrder: !!quoteTableState.tableState.sortBy[0]
      ? quoteTableState.tableState.sortBy[0].desc
        ? 'desc'
        : 'asc'
      : undefined,
    sortBy: !!quoteTableState.tableState.sortBy[0]
      ? (quoteTableState.tableState.sortBy[0].id as SortableFields)
      : undefined,
    search: {
      quoteNumber: quoteTableState.searchFilters.quoteNumber,
      wholesalerCustomerNumber: quoteTableState.searchFilters.wholesalerCustomerNumber,
      jobInfoProjectName: quoteTableState.searchFilters.jobInfoProjectName,
      plumberProjectType: quoteTableState.searchFilters.plumberProjectType,
      wholesaler: quoteTableState.searchFilters.wholesaler,
      plumberContractor: quoteTableState.searchFilters.plumberContractor,
      brand: quoteTableState.searchFilters.brand,
      repAgency: quoteTableState.searchFilters.repAgency,
      requestedBy: quoteTableState.searchFilters.requestedBy,
      status: quoteTableState.searchFilters.status,
      approvedBy: quoteTableState.searchFilters.approvedBy,
      revisionApprovedBy: quoteTableState.searchFilters.revisionApprovedBy,
      dateSubmitted: quoteTableState.searchFilters.dateSubmitted,
      dateApproved: quoteTableState.searchFilters.dateApproved,
      dateRevised: quoteTableState.searchFilters.dateRevised,
      lastModifiedBy: quoteTableState.searchFilters.lastModifiedBy,
      jobInfoStateName: quoteTableState.searchFilters.jobInfoStateName,
    },
    userEmail: userEmail,
    userId: userId,
  });

  return (
    <quoteListContext.Provider
      value={{
        quoteTableState,
        setQuoteTableState,
        formSearchFilters,
        setFormSearchFilters,
        quoteIdToDelete,
        setQuoteIdToDelete,
        quotes,
        isFetching,
        isLoading,
        dialogOpen,
        toggleDialog,
        deleteDialogOpen,
        toggleDeleteDialog,
      }}
    >
      {children}
    </quoteListContext.Provider>
  );
}

export function useQuoteListState() {
  return useContext(quoteListContext);
}
